import {
    Avatar,
    Box,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import React, { useContext, useMemo, useState } from "react";
import AuthContext from "../context/AuthContext";
import Logout from '@mui/icons-material/Logout';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { ColorModeContext } from "../theme/ThemeProviderWrapper";
import SunnyIcon from '@mui/icons-material/Sunny';
import { useLocation, useNavigate } from "react-router-dom";
import LogoutPopup from "../popup/Logout";
import { usePopups } from "../hooks/UsePopups";
import { useConstants } from "../hooks/UseConstants";
import { useMainLayout } from "../hooks/UseMainLayout";
import { APP_NAME } from "../constants/brand";
import { useSidebarDrawer } from "../context/SidebarDrawerContext";

function resolvePageMeta(pathname, isAr) {
    const routes = [
        { match: /^\/dashboard\/?$/, title: isAr ? 'لوحة التحكم' : 'Dashboard', subtitle: isAr ? 'نظرة عامة على أداء التدريس' : 'Teaching performance overview' },
        { match: /^\/notifications\/?$/, title: isAr ? 'الإشعارات' : 'Notifications', subtitle: isAr ? 'آخر التحديثات والتنبيهات' : 'Latest updates and alerts' },
        { match: /^\/students\/?$/, title: isAr ? 'الطلاب' : 'Students', subtitle: isAr ? 'درجات وتقدم الطلاب' : 'Student grades and progress' },
        { match: /^\/financial\/?$/, title: isAr ? 'المالية' : 'Financial', subtitle: isAr ? 'الأرباح وطلبات السحب' : 'Earnings and withdrawals' },
        { match: /^\/profile\/?$/, title: isAr ? 'الملف الشخصي' : 'Profile', subtitle: isAr ? 'إعدادات حسابك' : 'Account settings' },
        { match: /^\/update-password\/?$/, title: isAr ? 'تغيير كلمة المرور' : 'Change password', subtitle: isAr ? 'أمان الحساب' : 'Account security' },
        { match: /^\/my-courses\/?$/, title: isAr ? 'دوراتي' : 'My courses', subtitle: isAr ? 'إدارة دوراتك المنشورة' : 'Manage your courses' },
        { match: /^\/create-course\/?$/, title: isAr ? 'إنشاء دورة' : 'Create course', subtitle: isAr ? 'أضف دورة جديدة' : 'Add a new course' },
        { match: /^\/update-course\//, title: isAr ? 'تعديل الدورة' : 'Update course', subtitle: isAr ? 'تحديث بيانات الدورة' : 'Edit course details' },
        { match: /\/add-file\/?$/, title: isAr ? 'إضافة فيديو' : 'Add video', subtitle: isAr ? 'رفع درس مرئي للدورة' : 'Upload a video lesson' },
        { match: /\/update_file\//, title: isAr ? 'تعديل الفيديو' : 'Update video', subtitle: isAr ? 'تحديث ملف الدرس' : 'Edit lesson file' },
        { match: /^\/course-details\//, title: isAr ? 'تفاصيل الدورة' : 'Course details', subtitle: isAr ? 'محتوى واختبارات الدورة' : 'Course content and exams' },
        { match: /^\/my-paths\/?$/, title: isAr ? 'مساراتي' : 'My paths', subtitle: isAr ? 'إدارة مسارات التعلّم' : 'Manage learning paths' },
        { match: /^\/path-details\//, title: isAr ? 'تفاصيل المسار' : 'Path details', subtitle: isAr ? 'دورات المسار' : 'Path courses' },
        { match: /^\/create-path\/?$/, title: isAr ? 'إنشاء مسار' : 'Create path', subtitle: isAr ? 'أضف مساراً تعليمياً' : 'Add a learning path' },
        { match: /^\/update-path\//, title: isAr ? 'تعديل المسار' : 'Update path', subtitle: isAr ? 'تحديث بيانات المسار' : 'Edit path details' },
        { match: /\/create-exam\/?$/, title: isAr ? 'إنشاء اختبار' : 'Create exam', subtitle: isAr ? 'أضف اختباراً للدورة' : 'Add course exam' },
        { match: /\/update-exam\//, title: isAr ? 'تعديل الاختبار' : 'Update exam', subtitle: isAr ? 'تحديث بيانات الاختبار' : 'Edit exam details' },
        { match: /\/questions\/?$/, title: isAr ? 'أسئلة الاختبار' : 'Exam questions', subtitle: isAr ? 'إدارة الأسئلة' : 'Manage questions' },
        { match: /\/create-question\/?$/, title: isAr ? 'إضافة سؤال' : 'Add question', subtitle: isAr ? 'سؤال جديد للاختبار' : 'New exam question' },
        { match: /\/update-question\//, title: isAr ? 'تعديل السؤال' : 'Update question', subtitle: isAr ? 'تحديث نص السؤال' : 'Edit question' },
        { match: /\/options\/?$/, title: isAr ? 'خيارات الإجابة' : 'Answer options', subtitle: isAr ? 'إدارة الخيارات' : 'Manage options' },
        { match: /\/create-option\/?$/, title: isAr ? 'إضافة خيار' : 'Add option', subtitle: isAr ? 'خيار إجابة جديد' : 'New answer option' },
        { match: /\/update-option\//, title: isAr ? 'تعديل الخيار' : 'Update option', subtitle: isAr ? 'تحديث الخيار' : 'Edit option' },
        { match: /\/add-file\/?$/, title: isAr ? 'إضافة ملف' : 'Add file', subtitle: isAr ? 'رفع محتوى تعليمي' : 'Upload learning content' },
        { match: /\/update_file\//, title: isAr ? 'تعديل الملف' : 'Update file', subtitle: isAr ? 'تحديث الملف' : 'Edit file' },
    ];

    const found = routes.find((r) => r.match.test(pathname));
    return found || {
        title: isAr ? 'لوحة المدرس' : 'Teacher dashboard',
        subtitle: isAr ? `منصة ${APP_NAME} التعليمية` : `${APP_NAME} platform`,
    };
}

function Header({ isFullWidth = false }) {
    const { language } = useConstants();
    const { areaSx } = useMainLayout();
    const { wait, profile } = useContext(AuthContext);
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { openDrawer } = useSidebarDrawer();
    const { setPopup } = usePopups();
    const colorMode = useContext(ColorModeContext);
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isAr = language === 'ar';

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const pageMeta = useMemo(() => resolvePageMeta(pathname, isAr), [pathname, isAr]);

    const iconBtnSx = {
        width: 46,
        height: 46,
        borderRadius: '13px',
        color: isDark ? '#93c5fd' : '#0D358C',
        bgcolor: isDark ? 'rgba(147, 197, 253, 0.1)' : 'rgba(13, 53, 140, 0.07)',
        border: `1px solid ${isDark ? 'rgba(147, 197, 253, 0.18)' : 'rgba(13, 53, 140, 0.1)'}`,
        transition: 'all 0.25s ease',
        '&:hover': {
            bgcolor: isDark ? 'rgba(147, 197, 253, 0.18)' : 'rgba(13, 53, 140, 0.12)',
            transform: 'translateY(-1px)',
            boxShadow: isDark ? '0 6px 16px rgba(0,0,0,0.25)' : '0 6px 16px rgba(13, 53, 140, 0.12)',
        },
    };

    return (
        <>
            {!wait && profile && (
                <React.Fragment>
                    <Box
                        component="header"
                        className={`student-header ${isFullWidth ? '!w-full px-4' : 'main-layout-content main-layout-with-sidebar'}`}
                        dir={language === 'en' ? 'ltr' : 'rtl'}
                        sx={{
                            float: language === 'en' ? 'right' : undefined,
                            width: isFullWidth ? '100% !important' : undefined,
                            ...(isFullWidth ? {} : areaSx),
                            bgcolor: isDark ? 'rgba(15, 23, 42, 0.92)' : 'rgba(255, 255, 255, 0.88)',
                            backdropFilter: 'blur(14px)',
                            borderBottom: `1px solid ${isDark ? 'rgba(148, 163, 184, 0.12)' : 'rgba(13, 53, 140, 0.08)'}`,
                            boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.35)' : '0 8px 28px rgba(13, 53, 140, 0.07)',
                        }}
                    >
                        <Box
                            className="student-header__inner flex justify-between items-center gap-3"
                            sx={{ py: { xs: 3, sm: 3.25, md: 3.5 }, minHeight: { xs: 72, md: 80 } }}
                        >
                            <Box sx={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                {isMobile && !isFullWidth ? (
                                    <Tooltip title={isAr ? 'القائمة' : 'Menu'}>
                                        <IconButton onClick={openDrawer} sx={iconBtnSx} aria-label="open menu">
                                            <MenuIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                ) : null}
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography
                                    variant="h5"
                                    className="student-header__title"
                                    sx={{
                                        fontWeight: 800,
                                        letterSpacing: isAr ? 0 : '-0.02em',
                                        lineHeight: 1.25,
                                        fontSize: { xs: '1.2rem', sm: '1.35rem', md: '1.5rem' },
                                        background: isDark
                                            ? 'linear-gradient(135deg, #bfdbfe 0%, #60a5fa 55%, #93c5fd 100%)'
                                            : 'linear-gradient(135deg, #0D358C 0%, #2563EB 52%, #457b9d 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                    }}
                                >
                                    {pageMeta.title}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    className="student-header__subtitle max-sm:truncate"
                                    sx={{
                                        mt: 0.4,
                                        color: isDark ? 'rgba(203, 213, 225, 0.75)' : '#64748b',
                                        fontWeight: 500,
                                        fontSize: { xs: '0.85rem', md: '0.925rem' },
                                    }}
                                >
                                    {pageMeta.subtitle}
                                </Typography>
                                </Box>
                            </Box>

                            <Box className="flex items-center gap-1.5 max-sm:gap-1 shrink-0">
                                <Tooltip title={isAr ? 'الإشعارات' : 'Notifications'}>
                                    <IconButton onClick={() => navigate('/notifications')} sx={iconBtnSx} aria-label="notifications">
                                        <NotificationsNoneOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={isDark ? (isAr ? 'الوضع الفاتح' : 'Light mode') : (isAr ? 'الوضع الداكن' : 'Dark mode')}>
                                    <IconButton onClick={colorMode.toggleColorMode} sx={iconBtnSx} aria-label="toggle theme">
                                        {isDark ? <SunnyIcon fontSize="small" /> : <WbSunnyOutlinedIcon fontSize="small" />}
                                    </IconButton>
                                </Tooltip>

                                <Box
                                    className="student-header__user flex items-center cursor-pointer max-sm:pl-1"
                                    onClick={(e) => setAnchorEl(e.currentTarget)}
                                    sx={{
                                        ml: { xs: 0.5, sm: 1 },
                                        pl: 0.75,
                                        pr: 1.5,
                                        py: 0.65,
                                        borderRadius: '999px',
                                        bgcolor: isDark ? 'rgba(30, 41, 59, 0.85)' : 'rgba(13, 53, 140, 0.05)',
                                        border: `1px solid ${isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(13, 53, 140, 0.12)'}`,
                                        transition: 'all 0.25s ease',
                                        '&:hover': {
                                            bgcolor: isDark ? 'rgba(51, 65, 85, 0.9)' : 'rgba(13, 53, 140, 0.09)',
                                            boxShadow: isDark ? '0 4px 14px rgba(0,0,0,0.3)' : '0 4px 14px rgba(13, 53, 140, 0.1)',
                                        },
                                    }}
                                >
                                    {profile.image ? (
                                        <Avatar src={profile.image} alt="" sx={{ width: 42, height: 42, border: '2px solid rgba(37, 99, 235, 0.35)' }} />
                                    ) : (
                                        <Avatar sx={{ width: 42, height: 42, bgcolor: '#0D358C', border: '2px solid rgba(37, 99, 235, 0.35)' }}>
                                            <PermIdentityIcon fontSize="small" />
                                        </Avatar>
                                    )}
                                    <Box className="mx-2 max-sm:hidden" sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={700} sx={{ color: isDark ? '#f1f5f9' : '#0f172a', lineHeight: 1.2, fontSize: '0.95rem' }} noWrap>
                                            {profile.first_name} {profile.last_name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 500, fontSize: '0.8rem' }}>
                                            {isAr ? 'مدرس' : 'Teacher'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={() => setAnchorEl(null)}
                        onClick={() => setAnchorEl(null)}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        dir="rtl"
                        PaperProps={{
                            sx: {
                                mt: 1,
                                minWidth: 190,
                                borderRadius: 2,
                                border: `1px solid ${isDark ? 'rgba(148, 163, 184, 0.15)' : 'rgba(13, 53, 140, 0.1)'}`,
                                boxShadow: '0 12px 32px rgba(13, 53, 140, 0.12)',
                            },
                        }}
                    >
                        <MenuItem onClick={() => navigate('/profile')}>
                            <ListItemIcon><PermIdentityIcon fontSize="small" sx={{ color: '#0D358C' }} /></ListItemIcon>
                            {isAr ? 'الملف الشخصي' : 'Profile'}
                        </MenuItem>
                        <MenuItem onClick={() => navigate('/update-password')}>
                            <ListItemIcon><LockOutlinedIcon fontSize="small" sx={{ color: '#0D358C' }} /></ListItemIcon>
                            {isAr ? 'تغيير كلمة المرور' : 'Change Password'}
                        </MenuItem>
                        <MenuItem className="!text-red-500" onClick={() => { setPopup('logout', 'flex'); }}>
                            <ListItemIcon className="!text-red-500"><Logout fontSize="small" /></ListItemIcon>
                            {isAr ? 'تسجيل الخروج' : 'Logout'}
                        </MenuItem>
                    </Menu>

                    <Box id="logout" className="w-screen h-screen fixed top-0 bg-gray-200 bg-opacity-5 hidden justify-center items-center max-sm:left-0" sx={{ zIndex: 1000 }}>
                        <LogoutPopup onClickCancel={() => setPopup('logout', 'none')} />
                    </Box>
                </React.Fragment>
            )}
        </>
    );
}

export default Header;
